import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import postService from '../services/postService';

const PostTitleManager = ({
                              currentPost,
                              onPostUpdate,
                              onError,
                              onSuccess,
                              className = ''
                          }) => {
    // Title editing state - isolated from post state to prevent re-renders
    const [titleEditState, setTitleEditState] = useState({
        isEditing: false,
        value: '',
        originalValue: ''
    });

    // Effect to sync titleEditState when currentPost changes
    useEffect(() => {
        if (currentPost && !titleEditState.isEditing) {
            setTitleEditState({
                isEditing: false,
                value: currentPost.title,
                originalValue: currentPost.title
            });
        }
    }, [currentPost, titleEditState.isEditing]);

    // Start editing the post title
    const startTitleEdit = () => {
        if (currentPost) {
            setTitleEditState({
                isEditing: true,
                value: currentPost.title,
                originalValue: currentPost.title
            });
        }
    };

    // Handle title input change efficiently - completely isolated from post state
    const handleTitleChange = (e) => {
        setTitleEditState(prev => ({
            ...prev,
            value: e.target.value
        }));
    };

    // Cancel title editing
    const cancelTitleEdit = () => {
        setTitleEditState({
            isEditing: false,
            value: titleEditState.originalValue, // Revert to original value
            originalValue: titleEditState.originalValue
        });
    };

    // Save the edited post title
    const savePostTitle = async () => {
        const newTitle = titleEditState.value.trim();

        if (!newTitle) {
            if (onError) {
                onError('Title cannot be empty.');
            }
            return;
        }

        // If the title hasn't changed, just exit edit mode
        if (newTitle === titleEditState.originalValue) {
            cancelTitleEdit();
            return;
        }

        try {
            // Update the post via API
            const updatedPost = await postService.updatePost(currentPost.id, {
                ...currentPost,
                title: newTitle
            });

            // Update parent component
            if (onPostUpdate) {
                onPostUpdate(updatedPost);
            }

            // Exit edit mode
            cancelTitleEdit();

            if (onSuccess) {
                onSuccess('Post title updated successfully');
            }
        } catch (err) {
            if (onError) {
                onError('Failed to update post title: ' + (err.message || 'Unknown error'));
            }
        }
    };

    // Handle key press in title edit input
    const handleTitleKeyPress = (e) => {
        if (e.key === 'Enter') {
            savePostTitle();
        } else if (e.key === 'Escape') {
            cancelTitleEdit();
        }
    };

    // Get post status info
    const getPostStatusInfo = (post) => {
        return {
            isPublished: post.is_published,
            publishedAt: post.published_at,
            status: post.is_published ? 'published' : 'draft',
            statusText: post.is_published ? 'Published' : 'Draft',
            statusClass: post.is_published ? 'published' : 'draft'
        };
    };

    if (!currentPost) {
        return null;
    }

    const statusInfo = getPostStatusInfo(currentPost);

    return (
        <div className={`post-title-row ${className}`}>
            {titleEditState.isEditing ? (
                <div className="title-edit-container">
                    <input
                        type="text"
                        className="title-edit-input"
                        value={titleEditState.value}
                        onChange={handleTitleChange}
                        onKeyDown={handleTitleKeyPress}
                        autoFocus
                        ref={input => input && input.focus()}
                    />
                    <div className="title-edit-actions">
                        <button
                            className="title-save-btn"
                            onClick={savePostTitle}
                            title="Save title"
                        >
                            <FaCheck />
                        </button>
                        <button
                            className="title-cancel-btn"
                            onClick={cancelTitleEdit}
                            title="Cancel"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>
            ) : (
                <h2
                    onDoubleClick={startTitleEdit}
                    className="editable-title"
                    title="Double-click to edit"
                >
                    {currentPost.title}
                </h2>
            )}
            <span className={`status-badge ${statusInfo.statusClass}`}>
        {statusInfo.statusText}
      </span>
        </div>
    );
};

export default PostTitleManager;